import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ExpandableText from "../ExpandableText";
import GearSuggestions from "../GearSuggestions";
import TripChecklist from "../TripChecklist";
import TripWeatherCard from "../TripWeatherCard";
import PastTripSummaryPanel from "./PastTripSummaryPanel";
import { searchMapLocations } from "../../utils/mapLocationSearch";
import {
  CalendarDays,
  CheckCircle2,
  Fish,
  Leaf,
  Map as MapIcon,
  MapPin,
  StickyNote,
  Waves,
} from "lucide-react";

const MONTH_MAP = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

function getMonthNumber(monthName) {
  if (!monthName || typeof monthName !== "string") return -1;
  // slice(0, 3) handles both "Sep" and "Sept" (and full "September").
  const key = monthName.trim().slice(0, 3).toLowerCase();
  return key in MONTH_MAP ? MONTH_MAP[key] : -1;
}

function parseSeasonRange(seasonKey) {
  if (!seasonKey || typeof seasonKey !== "string") return null;
  const parts = seasonKey.split("-").map((p) => p.trim());
  if (parts.length !== 2) return null;
  const start = getMonthNumber(parts[0]);
  const end = getMonthNumber(parts[1]);
  if (start < 0 || end < 0) return null;
  return { start, end };
}

function isMonthInSeason(month, seasonKey) {
  const range = parseSeasonRange(seasonKey);
  if (!range) return false;
  const { start, end } = range;
  if (start <= end) return month >= start && month <= end;
  // Wrap-around range (e.g. Nov-Apr, Dec-Mar, Oct-Apr).
  return month >= start || month <= end;
}

function normalizeLocationName(str = "") {
  return String(str)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function locationsMatch(userLoc, dataLoc) {
  const a = normalizeLocationName(userLoc);
  const b = normalizeLocationName(dataLoc);
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.includes(b) || b.includes(a)) return true;

  // Token-subset: simplifyPlaceName can produce "Tolo, Peloponnese, Greece"
  // while trip-data.json stores "Tolo, Greece". Raw substring misses that.
  // Instead, require every token of the shorter side to appear in the longer.
  const at = a.split(" ").filter(Boolean);
  const bt = b.split(" ").filter(Boolean);
  if (!at.length || !bt.length) return false;
  const [shortTokens, longTokens] =
    at.length <= bt.length ? [at, bt] : [bt, at];
  const longSet = new Set(longTokens);
  return shortTokens.every((token) => longSet.has(token));
}

function normalizeSpeciesName(str = "") {
  // Strip parenthetical qualifiers so "Mahi-mahi (Dorado)" matches "Mahi-Mahi".
  return normalizeLocationName(String(str).replace(/\s*\(.*?\)/g, ""));
}

function resolveTripMonthNumber(currentTrip) {
  const direct = getMonthNumber(currentTrip?.month);
  if (direct >= 0) return direct;
  // Fallback for non-English month labels or missing month: derive from startDate.
  if (currentTrip?.startDate) {
    const d = new Date(currentTrip.startDate);
    if (!Number.isNaN(d.getTime())) return d.getMonth();
  }
  return -1;
}

function findMatchingSpecies(currentTrip, fishData) {
  if (!currentTrip || !fishData || typeof fishData !== "object") return [];

  const userLocation = currentTrip.location || "";
  if (!userLocation) return [];

  const targetSpecies = normalizeSpeciesName(currentTrip.species || "");
  const targetMonth = resolveTripMonthNumber(currentTrip);

  const matches = new Set();

  for (const [speciesName, seasons] of Object.entries(fishData)) {
    if (!seasons || typeof seasons !== "object") continue;
    if (normalizeSpeciesName(speciesName) === targetSpecies) continue;

    for (const [seasonKey, locs] of Object.entries(seasons)) {
      if (!Array.isArray(locs)) continue;
      if (targetMonth >= 0 && !isMonthInSeason(targetMonth, seasonKey)) continue;

      if (locs.some((loc) => locationsMatch(userLocation, loc))) {
        matches.add(speciesName);
        break;
      }
    }
  }

  return Array.from(matches);
}

function formatPlannedTripDate(dateValue) {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatPlannedTripRange(trip) {
  const start = formatPlannedTripDate(trip?.startDate);
  const end = formatPlannedTripDate(trip?.endDate);
  if (start && end) return `${start} -> ${end}`;
  return start || end;
}

function toFiniteCoordinate(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function normalizeLatLng(latValue, lngValue) {
  const lat = toFiniteCoordinate(latValue);
  const lng = toFiniteCoordinate(lngValue);
  if (lat === null || lng === null) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
}

function resolveLatLngFromValue(value) {
  if (!value) return null;

  if (typeof value === "string") {
    const matches = value.match(/-?\d+(?:\.\d+)?/g);
    if (matches && matches.length >= 2) {
      return normalizeLatLng(matches[0], matches[1]);
    }
    return null;
  }

  if (Array.isArray(value) && value.length >= 2) {
    return normalizeLatLng(value[0], value[1]);
  }

  if (typeof value !== "object") return null;

  return (
    normalizeLatLng(value.lat, value.lng) ||
    normalizeLatLng(value.lat, value.lon) ||
    normalizeLatLng(value.latitude, value.longitude) ||
    resolveLatLngFromValue(value.coordinates) ||
    resolveLatLngFromValue(value.coords)
  );
}

function resolveTripMapCoordinates(trip) {
  if (!trip || typeof trip !== "object") return null;

  return (
    normalizeLatLng(trip.lat, trip.lng) ||
    normalizeLatLng(trip.lat, trip.lon) ||
    normalizeLatLng(trip.latitude, trip.longitude) ||
    resolveLatLngFromValue(trip.coordinates) ||
    resolveLatLngFromValue(trip.coords) ||
    resolveLatLngFromValue(trip.destinationCoordinates) ||
    resolveLatLngFromValue(trip.destinationCoords) ||
    resolveLatLngFromValue(trip.locationCoordinates) ||
    resolveLatLngFromValue(trip.locationCoords) ||
    resolveLatLngFromValue(trip.mapLocation) ||
    resolveLatLngFromValue(trip.place) ||
    resolveLatLngFromValue(trip.selectedLocation)
  );
}

function getPlannedTripLocationQuery(trip) {
  return String(
    trip?.location ||
      trip?.destination ||
      trip?.destinationName ||
      trip?.placeName ||
      ""
  ).trim();
}

function getSpeciesIconSlug(speciesName = "") {
  return String(speciesName)
    .replace(/\s*\(.*?\)/g, "")
    .replace(/_/g, "-")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function getSpeciesLookupKeys(value = "") {
  const raw = String(value || "").trim();
  if (!raw) return [];

  const keys = new Set();
  const addKey = (candidate) => {
    const normalized = normalizeSpeciesName(candidate);
    if (normalized) keys.add(normalized);
  };

  addKey(raw);
  addKey(raw.replace(/_/g, " "));

  const parentheticalMatch = raw.match(/^(.+?)\s*\(([^)]+)\)$/);
  if (parentheticalMatch) {
    addKey(parentheticalMatch[1]);
    addKey(parentheticalMatch[2]);
  }

  raw.split(/\s*\/\s*/).forEach(addKey);

  return Array.from(keys);
}

const PLANNED_TRIP_SPECIES_ICON_ALIASES = {
  "mahi mahi": "coryphaena-hippurus",
  dorado: "coryphaena-hippurus",
  "mahi mahi dorado": "coryphaena-hippurus",
  "european sea bass": "dicentrarchus-labrax",
  "european seabass": "dicentrarchus-labrax",
  "european barracuda": "sphyraena-sphyraena",
  "horse mackerel": "trachurus-trachurus",
};

function findPlannedTripSpeciesIconAlias(species) {
  for (const key of getSpeciesLookupKeys(species)) {
    const iconSlug = PLANNED_TRIP_SPECIES_ICON_ALIASES[key];
    if (iconSlug) return iconSlug;
  }

  return null;
}

function addSpeciesLookupEntry(lookup, rawValue, entryPatch) {
  getSpeciesLookupKeys(rawValue).forEach((key) => {
    const existing = lookup.get(key) || {};
    lookup.set(key, { ...existing, ...entryPatch });
  });
}

function createTripDataV2Lookup() {
  return Object.create(null);
}

function addTripDataV2LookupEntry(lookup, rawValue, entry) {
  getSpeciesLookupKeys(rawValue).forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(lookup, key)) {
      lookup[key] = entry;
    }
  });
}

function buildTripDataV2Lookup(data) {
  const lookup = createTripDataV2Lookup();
  const speciesList = Array.isArray(data?.species) ? data.species : [];

  speciesList.forEach((entry) => {
    if (!entry || typeof entry !== "object") return;

    addTripDataV2LookupEntry(lookup, entry.id, entry);
    addTripDataV2LookupEntry(lookup, entry.legacyName, entry);
    addTripDataV2LookupEntry(lookup, entry.displayName, entry);
    addTripDataV2LookupEntry(lookup, entry.commonName, entry);
    addTripDataV2LookupEntry(lookup, entry.scientificName, entry);
    if (Array.isArray(entry.aliases)) {
      entry.aliases.forEach((alias) => addTripDataV2LookupEntry(lookup, alias, entry));
    }
  });

  return lookup;
}

function buildSpeciesIconCatalog(fishList, detailJsons, tripSpeciesData) {
  const lookup = new window.Map();

  if (tripSpeciesData && typeof tripSpeciesData === "object") {
    Object.entries(tripSpeciesData).forEach(([canonicalId, tripSpecies]) => {
      if (!tripSpecies || typeof tripSpecies !== "object") return;

      const patch = {
        tripSpecies: {
          ...tripSpecies,
          canonicalId,
        },
      };

      addSpeciesLookupEntry(lookup, canonicalId, patch);
      addSpeciesLookupEntry(lookup, tripSpecies.legacyName, patch);
      addSpeciesLookupEntry(lookup, tripSpecies.displayName, patch);
      addSpeciesLookupEntry(lookup, tripSpecies.commonName, patch);
      addSpeciesLookupEntry(lookup, tripSpecies.scientificName, patch);
      if (Array.isArray(tripSpecies.aliases)) {
        tripSpecies.aliases.forEach((alias) =>
          addSpeciesLookupEntry(lookup, alias, patch)
        );
      }
    });
  }

  detailJsons.forEach((detailsJson) => {
    const speciesRecord = detailsJson?.species;
    if (!speciesRecord || typeof speciesRecord !== "object") return;

    Object.values(speciesRecord).forEach((detail) => {
      if (!detail || typeof detail !== "object") return;
      const patch = { detail };
      addSpeciesLookupEntry(lookup, detail.id, patch);
      addSpeciesLookupEntry(lookup, detail.commonName, patch);
      addSpeciesLookupEntry(lookup, detail.displayName, patch);
      addSpeciesLookupEntry(lookup, detail.scientificName, patch);
      if (Array.isArray(detail.aliases)) {
        detail.aliases.forEach((alias) => addSpeciesLookupEntry(lookup, alias, patch));
      }
    });
  });

  if (Array.isArray(fishList)) {
    fishList.forEach((fish) => {
      if (!fish || typeof fish !== "object") return;
      const patch = { fish };
      addSpeciesLookupEntry(lookup, fish.id, patch);
      addSpeciesLookupEntry(lookup, fish.name, patch);
      addSpeciesLookupEntry(lookup, fish.commonName, patch);
      addSpeciesLookupEntry(lookup, fish.common_name, patch);
      addSpeciesLookupEntry(lookup, fish.scientificName, patch);
    });
  }

  return lookup;
}

function findSpeciesIconCatalogEntry(species, catalog) {
  if (!catalog) return null;

  for (const key of getSpeciesLookupKeys(species)) {
    const entry = catalog.get(key);
    if (entry) return entry;
  }

  return null;
}

function buildSpeciesIconCandidates(species, catalog) {
  const entry = findSpeciesIconCatalogEntry(species, catalog);
  const slugs = [];
  const iconPaths = [];
  const pushIconPath = (value) => {
    if (typeof value !== "string" || !value.startsWith("/")) return;
    if (!iconPaths.includes(value)) {
      iconPaths.push(value);
    }
  };
  const pushSlug = (value) => {
    const slug = getSpeciesIconSlug(value);
    if (slug && !slugs.includes(slug)) {
      slugs.push(slug);
    }
  };

  pushIconPath(entry?.tripSpecies?.iconPath);
  pushSlug(entry?.tripSpecies?.canonicalId);
  pushSlug(entry?.tripSpecies?.scientificName);
  pushSlug(entry?.detail?.scientificName);
  pushSlug(entry?.detail?.id);
  pushSlug(entry?.fish?.scientificName);
  pushSlug(findPlannedTripSpeciesIconAlias(species));
  pushSlug(species);

  return [
    ...iconPaths,
    ...slugs.map((slug) => `/icons/${slug}-icon.png`),
  ].filter((path, index, paths) => paths.indexOf(path) === index);
}

function findTripDataV2Entry(species, lookup) {
  if (!lookup) return null;

  for (const key of getSpeciesLookupKeys(species)) {
    const entry = lookup[key];
    if (entry) return entry;
  }

  return null;
}

async function fetchOptionalJson(path) {
  const response = await fetch(path);
  if (!response.ok) {
    throw new Error(`Failed to load ${path}: ${response.status}`);
  }
  return response.json();
}

function SpeciesCardIcon({ species, catalog, iconPath }) {
  const [didIconPathFail, setDidIconPathFail] = React.useState(false);
  const iconCandidates = React.useMemo(
    () => buildSpeciesIconCandidates(species, catalog),
    [catalog, species]
  );
  const iconCandidateKey = iconCandidates.join("|");
  const [failedIcons, setFailedIcons] = React.useState(() => new Set());

  React.useEffect(() => {
    setDidIconPathFail(false);
    setFailedIcons(new Set());
  }, [iconCandidateKey, iconPath]);

  if (iconPath && !didIconPathFail) {
    return (
      <img
        src={iconPath}
        alt=""
        aria-hidden="true"
        onError={() => setDidIconPathFail(true)}
      />
    );
  }

  const iconSrc = iconCandidates.find((candidate) => !failedIcons.has(candidate));

  if (!iconSrc) {
    return <Fish size={27} strokeWidth={2.2} aria-hidden="true" />;
  }

  return (
    <img
      src={iconSrc}
      alt=""
      aria-hidden="true"
      onError={() =>
        setFailedIcons((current) => {
          const next = new Set(current);
          next.add(iconSrc);
          return next;
        })
      }
    />
  );
}

function TripTravelButtons({ location, t }) {
  return (
    <div className="mytrips-travel-buttons">
      <button
        onClick={() => window.open("https://www.skyscanner.net", "_blank")}
        className="mytrips-travel-btn mytrips-flights-btn"
      >
        ✈ {t("trip_find_flights")}
      </button>

      <button
        onClick={() =>
          window.open(
            `https://www.booking.com/searchresults.html?ss=${encodeURIComponent(
              location
            )}`,
            "_blank"
          )
        }
        className="mytrips-travel-btn mytrips-hotels-btn"
      >
        🏨 {t("trip_find_hotels")}
      </button>

      <button
        onClick={() => window.open("https://surfshark.com", "_blank")}
        className="mytrips-travel-btn mytrips-vpn-btn"
      >
        🔐 {t("trip_vpn")}
      </button>

      <button
        onClick={() => window.open("https://esim.holafly.com", "_blank")}
        className="mytrips-travel-btn mytrips-esim-btn"
      >
        📱 {t("trip_esim")}
      </button>
    </div>
  );
}

export default function TripDetailsPanel({
  panel,
  selectedTrip,
  userId,
  fishData,
  fishingMethod,
  weatherConditions,
  onRefreshWeather,
  setSelectedTrip,
  selectedWeatherDay,
  onSelectedWeatherDayChange,
  pastSummaryProps,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const speciesScrollerRef = React.useRef(null);
  const [speciesIconCatalog, setSpeciesIconCatalog] = React.useState(null);
  const [tripDataV2Lookup, setTripDataV2Lookup] = React.useState(() =>
    createTripDataV2Lookup()
  );
  const [isFindingMapLocation, setIsFindingMapLocation] = React.useState(false);
  const [mapLocationError, setMapLocationError] = React.useState("");
  const [isDesktopSpeciesViewport, setIsDesktopSpeciesViewport] =
    React.useState(() =>
      typeof window !== "undefined" && typeof window.matchMedia === "function"
        ? window.matchMedia("(min-width: 769px)").matches
        : false
    );
  const [isDesktopSpeciesExpanded, setIsDesktopSpeciesExpanded] =
    React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(min-width: 769px)");
    const handleMediaChange = (event) => {
      setIsDesktopSpeciesViewport(event.matches);
    };

    setIsDesktopSpeciesViewport(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleMediaChange);
      return () => mediaQuery.removeEventListener("change", handleMediaChange);
    }

    mediaQuery.addListener(handleMediaChange);
    return () => mediaQuery.removeListener(handleMediaChange);
  }, []);

  React.useEffect(() => {
    if (panel !== "planned" && panel !== "current") return undefined;

    let cancelled = false;

    async function loadSpeciesIconCatalog() {
      const [
        tripSpeciesResult,
        fishListResult,
        marineDetailsResult,
        freshwaterDetailsResult,
      ] =
        await Promise.allSettled([
          fetchOptionalJson("/JSON/trip-species-data.json"),
          fetchOptionalJson("/JSON/fish-data.json"),
          fetchOptionalJson("/fish-species/details/marine-details.json"),
          fetchOptionalJson("/fish-species/details/freshwater-details.json"),
        ]);

      if (cancelled) return;

      const fishList =
        fishListResult.status === "fulfilled" && Array.isArray(fishListResult.value)
          ? fishListResult.value
          : [];
      const detailJsons = [marineDetailsResult, freshwaterDetailsResult]
        .filter((result) => result.status === "fulfilled")
        .map((result) => result.value);
      const tripSpeciesData =
        tripSpeciesResult.status === "fulfilled" &&
        tripSpeciesResult.value &&
        typeof tripSpeciesResult.value === "object"
          ? tripSpeciesResult.value
          : {};

      setSpeciesIconCatalog(
        buildSpeciesIconCatalog(fishList, detailJsons, tripSpeciesData)
      );
    }

    loadSpeciesIconCatalog().catch(() => {
      if (!cancelled) {
        setSpeciesIconCatalog(buildSpeciesIconCatalog([], [], {}));
      }
    });

    fetchOptionalJson("/JSON/trip-data.v2.json")
      .then((json) => {
        if (!cancelled) {
          setTripDataV2Lookup(buildTripDataV2Lookup(json));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTripDataV2Lookup(createTripDataV2Lookup());
        }
      });

    return () => {
      cancelled = true;
    };
  }, [panel]);

  React.useEffect(() => {
    setIsFindingMapLocation(false);
    setMapLocationError("");
    setIsDesktopSpeciesExpanded(false);
  }, [panel, selectedTrip?.id]);

  if (!selectedTrip) {
    return <p className="mytrips-no-selection">{t("trip_select_prompt")}</p>;
  }

  const matches = fishData ? findMatchingSpecies(selectedTrip, fishData) : [];
  const isPast = panel === "past";
  const isPlanned = panel === "planned";
  const isCurrent = panel === "current";
  const usesModernMobileDetails = isPlanned || isCurrent;
  const usesCompactMobileSummary = usesModernMobileDetails || isPast;
  const plannedTripRange = formatPlannedTripRange(selectedTrip);
  const plannedTripMapCoordinates = isPlanned
    ? resolveTripMapCoordinates(selectedTrip)
    : null;
  const plannedTripLocationQuery = isPlanned
    ? getPlannedTripLocationQuery(selectedTrip)
    : "";
  const canViewPlannedTripOnMap =
    Boolean(plannedTripMapCoordinates) || plannedTripLocationQuery.length >= 3;
  const speciesOptions = [selectedTrip.species, ...matches].filter(Boolean);
  const shouldLimitDesktopSpecies =
    (isCurrent || isPlanned) &&
    isDesktopSpeciesViewport &&
    speciesOptions.length > 3;
  const visibleSpeciesOptions =
    shouldLimitDesktopSpecies && !isDesktopSpeciesExpanded
      ? speciesOptions.slice(0, 3)
      : speciesOptions;
  const showDesktopSpeciesSeeAll =
    shouldLimitDesktopSpecies && !isDesktopSpeciesExpanded;
  const showDesktopSpeciesShowLess =
    shouldLimitDesktopSpecies && isDesktopSpeciesExpanded;
  const selectSpecies = (species) => {
    setSelectedTrip((prev) => ({
      ...prev,
      species,
    }));
  };
  const handleViewAllSpecies = () => {
    speciesScrollerRef.current?.scrollBy({ left: 320, behavior: "smooth" });
  };
  const handleViewOnMap = async () => {
    if (!canViewPlannedTripOnMap || isFindingMapLocation) return;

    setMapLocationError("");
    setIsFindingMapLocation(true);

    let mapCoordinates = plannedTripMapCoordinates;
    let resolvedLocationLabel = plannedTripLocationQuery;

    if (!mapCoordinates) {
      try {
        const locations = await searchMapLocations(plannedTripLocationQuery, {
          limit: 1,
        });
        const firstLocation = locations[0];
        mapCoordinates = normalizeLatLng(firstLocation?.lat, firstLocation?.lng);
        resolvedLocationLabel = firstLocation?.label || plannedTripLocationQuery;
      } catch {
        mapCoordinates = null;
      }
    }

    if (!mapCoordinates) {
      setIsFindingMapLocation(false);
      setMapLocationError(
        t("trip_map_location_lookup_failed", {
          defaultValue:
            "We could not find that location on the map. Try opening Map and searching manually.",
        })
      );
      return;
    }

    const pendingLocation = {
      ...mapCoordinates,
      zoom: 10,
      openOverlay: true,
      source: "planned-trip",
      tripId: selectedTrip.id,
      title: selectedTrip.name || plannedTripLocationQuery,
      label: resolvedLocationLabel || selectedTrip.name || "",
      name: selectedTrip.name || selectedTrip.location || "",
      location: selectedTrip.location || "",
    };

    try {
      window.sessionStorage.setItem(
        "pendingMapLocation",
        JSON.stringify(pendingLocation)
      );
    } catch (error) {
      console.warn("Unable to persist planned trip map location", error);
    }

    setIsFindingMapLocation(false);

    navigate({
      pathname: "/",
      search: "?tab=map",
      hash: "",
    });

    if (typeof window !== "undefined") {
      window.setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("openMapAtLocation", {
            detail: pendingLocation,
          })
        );
      }, 0);
    }
  };

  return (
    <div
      className={`mytrips-details-panel-inner ${
        isPlanned ? "mytrips-details-panel-inner--planned" : ""
      } ${
        isCurrent ? "mytrips-details-panel-inner--current" : ""
      } ${
        isPast ? "mytrips-details-panel-inner--past" : ""
      }`}
    >
      <h2 className="mytrips-details-title">{selectedTrip.name}</h2>

      {usesCompactMobileSummary && (
        <section
          className={`mytrips-planned-detail-card ${
            isCurrent ? "mytrips-current-detail-card" : ""
          } ${
            isPast ? "mytrips-past-detail-card" : ""
          }`}
          aria-label={
            isCurrent
              ? t("trip_current")
              : isPast
                ? t("trip_past_detail_title")
              : t("planned_trip_detail_title")
          }
        >
          <div className="mytrips-planned-detail-card__main">
            <div className="mytrips-planned-detail-card__head">
              <h3>{selectedTrip.name}</h3>
              <span
                className={`mytrips-planned-detail-card__status ${
                  isCurrent
                    ? "mytrips-planned-detail-card__status--current"
                    : isPast
                      ? "mytrips-planned-detail-card__status--past"
                    : ""
                }`}
              >
                {isCurrent
                  ? t("trip_status_current")
                  : isPast
                    ? t("trip_status_past")
                  : t("trip_status_planned")}
              </span>
            </div>

            <div className="mytrips-planned-detail-card__facts">
              {plannedTripRange && (
                <div className="mytrips-planned-detail-card__fact">
                  <CalendarDays size={16} strokeWidth={2.3} aria-hidden="true" />
                  <span>{plannedTripRange}</span>
                </div>
              )}

              {selectedTrip.location && (
                <div className="mytrips-planned-detail-card__fact">
                  <MapPin size={16} strokeWidth={2.3} aria-hidden="true" />
                  <span>{selectedTrip.location}</span>
                </div>
              )}

              {selectedTrip.species && (
                <div className="mytrips-planned-detail-card__fact">
                  <Fish size={16} strokeWidth={2.3} aria-hidden="true" />
                  <span>{selectedTrip.species}</span>
                </div>
              )}

              {(isCurrent || isPast) && selectedTrip.season && (
                <div className="mytrips-planned-detail-card__fact">
                  <Leaf size={16} strokeWidth={2.3} aria-hidden="true" />
                  <span>{selectedTrip.season}</span>
                </div>
              )}

              {(isCurrent || isPast) && selectedTrip.fishingType && (
                <div className="mytrips-planned-detail-card__fact">
                  <Waves size={16} strokeWidth={2.3} aria-hidden="true" />
                  <span>{selectedTrip.fishingType}</span>
                </div>
              )}

              {(isCurrent || isPast) && selectedTrip.notes && (
                <div className="mytrips-planned-detail-card__fact mytrips-planned-detail-card__fact--wide">
                  <StickyNote size={16} strokeWidth={2.3} aria-hidden="true" />
                  <ExpandableText text={selectedTrip.notes} maxChars={220} />
                </div>
              )}
            </div>
          </div>

          {isPlanned && (
            <button
              type="button"
              className="mytrips-planned-detail-card__map"
              aria-label={t("trip_view_on_map", { defaultValue: "View on Map" })}
              title={
                canViewPlannedTripOnMap
                  ? t("trip_view_on_map", { defaultValue: "View on Map" })
                  : t("trip_map_coordinates_missing", {
                      defaultValue:
                        "Map location is not available for this planned trip.",
                    })
              }
              disabled={!canViewPlannedTripOnMap || isFindingMapLocation}
              onClick={handleViewOnMap}
            >
              <MapIcon size={18} strokeWidth={2.3} aria-hidden="true" />
              <span>
                {isFindingMapLocation
                  ? t("trip_map_finding_location", { defaultValue: "Finding..." })
                  : t("trip_view_on_map", { defaultValue: "View on Map" })}
              </span>
            </button>
          )}
          {isPlanned && mapLocationError && (
            <p className="mytrips-planned-detail-card__map-error" role="status">
              {mapLocationError}
            </p>
          )}
        </section>
      )}

      <TripWeatherCard
        conditions={weatherConditions}
        tripId={selectedTrip.id}
        userId={userId}
        allowRefresh={!isPast}
        isPastTripView={isPast}
        onRefresh={onRefreshWeather}
        selectedDay={selectedWeatherDay}
        onSelectedDayChange={onSelectedWeatherDayChange}
      />

      {selectedTrip.startDate && (
        <p className="mytrips-detail-legacy-fact">
          <strong>{t("trip_dates")}:</strong>{" "}
          {new Date(selectedTrip.startDate).toLocaleDateString()}{" "}
          {selectedTrip.endDate &&
            `→ ${new Date(selectedTrip.endDate).toLocaleDateString()}`}
        </p>
      )}

      {selectedTrip.location && (
        <p className="mytrips-detail-legacy-fact">
          <strong>{t("trip_location")}:</strong> {selectedTrip.location}
        </p>
      )}

      {selectedTrip.species && (
        <p className="mytrips-detail-legacy-fact">
          <strong>{t("trip_target_species")}:</strong> {selectedTrip.species}
        </p>
      )}

      {selectedTrip.fishingType && (
        <p className="mytrips-detail-legacy-fact">
          <strong>{t("trip_fishing_type")}:</strong> {selectedTrip.fishingType}
        </p>
      )}

      {selectedTrip.notes && (
        <div className="mytrips-notes">
          <strong>{t("trip_notes")}:</strong>{" "}
          <ExpandableText text={selectedTrip.notes} maxChars={220} />
        </div>
      )}

      {selectedTrip.location && <TripTravelButtons location={selectedTrip.location} t={t} />}

      {isPast && (
        <PastTripSummaryPanel
          trip={selectedTrip}
          weatherConditions={weatherConditions}
          {...pastSummaryProps}
        />
      )}

      <hr />

      {usesModernMobileDetails && matches.length > 0 && (
        <div
          className={`mytrips-also-active ${
            usesModernMobileDetails ? "mytrips-also-active--planned" : ""
          }`}
        >
          <div className="mytrips-also-active-mobile-head">
            <span className="mytrips-also-active-mobile-head__icon" aria-hidden="true">
              <Fish size={18} strokeWidth={2.3} />
            </span>
            <div>
              <h3>
                {isCurrent
                  ? t("trip_current_active_species_title")
                  : t("trip_also_active_period")}
              </h3>
              <p>
                {isCurrent
                  ? t("trip_current_active_species_subtitle")
                  : t("trip_also_active_subtitle")}
              </p>
            </div>
          </div>
          <h3 className="mytrips-also-active-title">
            🎣 {t("trip_also_active")}
          </h3>

          <p className="mytrips-also-active-desc">{t("trip_select_species")}</p>

          <div
            className={`mytrips-species-list${
              shouldLimitDesktopSpecies
                ? " mytrips-species-list--desktop-limited"
                : ""
            }${
              showDesktopSpeciesShowLess
                ? " mytrips-species-list--desktop-expanded"
                : ""
            }`}
            ref={speciesScrollerRef}
          >
            {visibleSpeciesOptions.map((species, i) => {
              const isSelected = selectedTrip.species === species;
              const speciesMeta = usesModernMobileDetails
                ? findTripDataV2Entry(species, tripDataV2Lookup)
                : null;
              const speciesLabel = speciesMeta?.legacyName || species;
              const speciesIconPath = speciesMeta?.iconPath || "";

              return (
              <label
                key={`${species}-${i}`}
                className={`mytrips-species-label ${
                  isSelected ? "is-selected" : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => selectSpecies(species)}
                  className="mytrips-species-checkbox"
                />
                <span className="mytrips-species-card-icon">
                  <SpeciesCardIcon
                    species={species}
                    catalog={speciesIconCatalog}
                    iconPath={speciesIconPath}
                  />
                </span>
                <span className="mytrips-species-card-copy">
                  <span className="mytrips-species-card-name">{speciesLabel}</span>
                </span>
                {isSelected && (
                  <span className="mytrips-species-card-check" aria-hidden="true">
                    <CheckCircle2 size={16} strokeWidth={2.4} />
                  </span>
                )}
                  {isSelected && (
                  <span className="mytrips-species-card-primary">
                    {t("trip_species_primary_badge", { defaultValue: "PRIMARY" })}
                  </span>
                  )}
                <span className="mytrips-species-legacy-name">{species}</span>
              </label>
              );
            })}
            {showDesktopSpeciesSeeAll && (
              <button
                type="button"
                className="mytrips-species-expand-card"
                onClick={() => setIsDesktopSpeciesExpanded(true)}
              >
                <span>{t("see_all", { defaultValue: "See all" })}</span>
                <strong>
                  {speciesOptions.length - visibleSpeciesOptions.length}{" "}
                  {t("more", { defaultValue: "more" })}
                </strong>
              </button>
            )}
            {showDesktopSpeciesShowLess && (
              <button
                type="button"
                className="mytrips-species-expand-card mytrips-species-expand-card--less"
                onClick={() => setIsDesktopSpeciesExpanded(false)}
              >
                <span>{t("show_less", { defaultValue: "Show less" })}</span>
              </button>
            )}
          </div>

          {usesModernMobileDetails &&
            !showDesktopSpeciesSeeAll &&
            !showDesktopSpeciesShowLess &&
            speciesOptions.length > 4 && (
            <button
              type="button"
              className="mytrips-species-view-all"
              onClick={handleViewAllSpecies}
            >
              {t("trip_view_all_species", { defaultValue: "View All Species" })}
            </button>
          )}
        </div>
      )}

      {isCurrent && (
        <div className="mytrips-current-todo mytrips-details-panel-inner--planned">
          <TripChecklist tripId={selectedTrip?.id} variant="current" />
        </div>
      )}

      <GearSuggestions
        selectedTrip={selectedTrip}
        fishingMethod={panel === "planned" ? null : fishingMethod}
        useDefaultMethod={usesModernMobileDetails}
      />

      {isPlanned && <TripChecklist tripId={selectedTrip?.id} />}
    </div>
  );
}
